// Generic constructor type
type Class<T = object> = new (...args: any[]) => T;

// Converts a union type to an intersection type
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void ? I : never;

/**
 * Mixes multiple classes (base class + mixins) into a new class.
 * - Copies instance properties from mixins into the new class
 * - Copies prototype methods (excluding constructors)
 *
 * @param Base - The main base class
 * @param mixins - One or more mixin classes to extend from
 * @returns A new class that combines all behaviors and types
 */
export const mixinClass = <Base extends Class, Mixins extends Class[]>(
  base: Base,
  ...mixins: Mixins
): new (
  ...args: ConstructorParameters<Base>
) => InstanceType<Base> & UnionToIntersection<InstanceType<Mixins[number]>> => {
  class Mixed extends base {
    constructor(...args: any[]) {
      super(...args);

      // Copy instance properties from mixins
      for (const mixin of mixins) {
        const instance = new mixin();
        Object.getOwnPropertyNames(instance).forEach((key) => {
          (this as any)[key] = instance[key as keyof typeof instance];
        });
      }
    }
  }

  // Copy prototype methods from mixins (excluding constructor)
  for (const mixin of mixins) {
    Object.getOwnPropertyNames(mixin.prototype).forEach((name) => {
      if (name !== 'constructor') {
        Object.defineProperty(Mixed.prototype, name, Object.getOwnPropertyDescriptor(mixin.prototype, name)!);
      }
    });
  }

  return Mixed as unknown as Class<InstanceType<Base> & UnionToIntersection<InstanceType<Mixins[number]>>>;
};
